import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { AlertTriangle, XCircle } from "lucide-react";
import BaseDialog from "../Modals/BaseDialog";
import { Button } from "@/components/shadcn/button";

import { ReactElement, ReactNode } from "react";
import { useRouter } from "next/navigation";

type CTADialogVariant = "error" | "warning";

interface CTADialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: CTADialogVariant;
  title: string;
  description?: string;
  content?: ReactNode;
  bannerTitle: string;
  buttonLabel: string;
  onClick: () => void;
  closable?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "full";
}

const variantConfig = {
  error: {
    icon: XCircle,
    iconColor: "text-red-500",
    bannerVariant: "destructive" as const,
  },
  warning: {
    icon: AlertTriangle,
    iconColor: "text-warning-500",
    bannerVariant: "warning" as const,
  },
};

const defaultTitle = "Missing embedding models";

const defaultContent = (
  <p>
    To use knowledge bases, you need at least one embedding model enabled.
    Embedding models are used to convert your documents into searchable vectors.
  </p>
);

export function CTADialog({
  isOpen,
  onOpenChange,
  variant = "warning",
  title,
  description,
  content = defaultContent,
  bannerTitle = defaultTitle,
  buttonLabel,
  onClick,
  closable = false,
  size = "md",
}: CTADialogProps) {
  const config = variantConfig[variant];
  const Icon = config.icon;

  const router = useRouter();
  const handleOnBack = () => {
    router.back();
  };

  return (
    <BaseDialog
      title={title}
      description={description}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      closable={closable}
      size={size}
    >
      <div className="space-y-4">
        {bannerTitle && (
          <EmbeddingsWarningBanner
            title={bannerTitle}
            variant={config.bannerVariant}
            icon={<Icon className={`h-4 w-4 ${config.iconColor}`} />}
          />
        )}
      </div>

      <div className="flex gap-4 mt-6">
        <Button onClick={handleOnBack} variant="outline" className="grow">
          Go back
        </Button>
        <Button onClick={onClick} className="grow">
          {buttonLabel}
        </Button>
      </div>
    </BaseDialog>
  );
}

export const EmbeddingsWarningBanner = ({
  title = defaultTitle,
  variant,
  icon,
}: {
  title?: string;
  variant: "warning" | "destructive";
  icon: ReactElement;
  hideText?: boolean;
}) => {
  return (
    <NoxusAlert
      look="grid"
      variant={variant}
      title={title}
      description="You need to configure embedding models in the Providers page to enable Knowledge Base functionalities."
      icon={icon}
    />
  );
};
